import React, { useEffect, useState, useRef } from 'react';
import axios from '../../api/axios';
import Select from "react-select";
import Flatpickr from "react-flatpickr";
import { toast } from 'react-toastify';
import { TrashIcon } from "@heroicons/react/24/outline";
import { useNavigate, useParams } from 'react-router-dom';
import Loader from '../../components/Loader';

const CreateSale = () => {
  const navigate = useNavigate();
  const { saleId } = useParams();
  const dropdownRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [items, setItems] = useState([]);           // catalog items (with sizes & colors)
  const [searchTerm, setSearchTerm] = useState('');

  const [saleDate, setSaleDate] = useState(new Date());
  const [customersOption, setCustomerOption] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [status, setStatus] = useState('quotation'); // final | quotation
  const [reference, setReference] = useState('');

  // sale lines:
  // { id, name, price, discount, quantity, total, sizes:[{size,quantity}], colors:[{color,quantity}] }
  const [saleItems, setSaleItems] = useState([]);

  const [discountType, setDiscountType] = useState('flat'); // percent | flat
  const [globalDiscount, setGlobalDiscount] = useState(0);
  const [otherCharges, setOtherCharges] = useState(0);
  const [note, setNote] = useState('');

  const [banks, setBanks] = useState([]);
  const [paymentBank, setPaymentBank] = useState("");

  const [paidAmount, setPaidAmount] = useState(0);      // existing paid (edit)
  const [newPaidAmount, setNewPaidAmount] = useState(0); // new payment input

  const [errors, setErrors] = useState({});

  const salesOptions = [
    { value: "final", label: "Final" },
    { value: "quotation", label: "Quotation" },
  ];

  // --- helpers ---
  const recalcLine = (line) => {
    // Sizes define the quantity; colors are descriptive only.
    const sizeQty = (line.sizes || []).reduce((s, r) => s + Number(r.quantity || 0), 0);
    const price = Number(line.price || 0);
    const disc  = Number(line.discount || 0);
    return {
      ...line,
      quantity: sizeQty,
      total: sizeQty * (price - disc),
    };
  };

  const colorsTotal = (line) =>
    (line.colors || []).reduce((s, c) => s + Number(c.quantity || 0), 0);

  // --- bootstrap ---
  useEffect(() => {
    // Load items first, then the rest, then sale (so we can seed sizes/colors properly)
    (async () => {
      try {
        const itemsRes = await axios.get('/items');
        const loadedItems = itemsRes.data || [];
        setItems(loadedItems);

        const [customersRes, banksRes] = await Promise.all([
          axios.get('/customers'),
          axios.get('/banks'),
        ]);

        setCustomerOption((customersRes.data || []).map(c => ({ value: c.id, label: c.name })));

        const bankOption = (banksRes.data || []).map(b => ({ value: b.id, label: b.name }));
        setBanks(bankOption);
        if (bankOption.length > 0) setPaymentBank(bankOption[0].value);

        if (saleId) {
          const { data: sale } = await axios.get(`/sales/${saleId}`);

          setSelectedCustomer(sale.customer_id);
          setStatus(sale.status);
          setReference(sale.reference);
          setSaleDate(new Date(sale.sale_date));
          setOtherCharges(Number(sale.other_charges || 0));
          setGlobalDiscount(Number(sale.discount || 0));
          setDiscountType(sale.discount_type || 'flat');
          setPaidAmount(Number(sale.paid_amount || 0));
          setNote(sale.note || '');
          setPaymentBank(sale.bank_id || bankOption[0]?.value || '');

          const mapped = (sale.items || []).map(it => {
            const id = Number(it.item_id);
            const price = Number(it.price || 0);
            const discount = Number(it.discount || 0);

            const catalog = loadedItems.find(i => i.id === id) || {};

            // Seed sizes/colors either from saved sale or from catalog defaults
            const savedSizes = Array.isArray(it.sizes) ? it.sizes : [];
            const savedColors = Array.isArray(it.colors) ? it.colors : [];

            const sizes = savedSizes.length
              ? savedSizes.map(s => ({ size: s.size, quantity: Number(s.quantity || 0) }))
              : (catalog.sizes || []).map(s => ({ size: s.size, quantity: 0 }));

            const colors = savedColors.length
              ? savedColors.map(c => ({ color: c.color, quantity: Number(c.quantity || 0) }))
              : (catalog.colors || []).map(c => ({ color: c.color, quantity: 0 }));

            return recalcLine({
              id,
              name: it.name || catalog.name,
              price,
              discount,
              sizes,
              colors,
            });
          });

          setSaleItems(mapped);
        }
      } catch (err) {
        console.error('Init error:', err);
        toast.error(err?.response?.data?.error || 'Failed to load data');
      }
    })();
  }, [saleId]);

  // close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- Add item ---
  const handleAddItem = (item) => {
    setSaleItems(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) {
        toast.info('Item already added');
        return prev;
      }

      const price = Number(item.sales_price || 0);

      const seedSizes = Array.isArray(item.sizes) && item.sizes.length
        ? item.sizes.map(s => ({ size: s.size, quantity: 0 }))
        : [{ size: '', quantity: 0 }];

      const seedColors = Array.isArray(item.colors) && item.colors.length
        ? item.colors.map(c => ({ color: c.color, quantity: 0 }))
        : [{ color: '', quantity: 0 }];

      const line = recalcLine({
        id: item.id,
        name: item.name,
        price,
        discount: 0,
        sizes: seedSizes,
        colors: seedColors,
      });
      return [...prev, line];
    });

    setSearchTerm('');
    setShowDropdown(false);
  };

  // --- Size & Color handlers ---
  const updateSizeQty = (itemId, sizeIndex, newQty) => {
    setSaleItems(prev =>
      prev.map(line => {
        if (line.id !== itemId) return line;
        const sizes = [...(line.sizes || [])];
        sizes[sizeIndex] = {
          ...sizes[sizeIndex],
          quantity: Number(newQty || 0),
        };
        return recalcLine({ ...line, sizes });
      })
    );
  };

  const updateSizeName = (itemId, sizeIndex, name) => {
    setSaleItems(prev =>
      prev.map(line => {
        if (line.id !== itemId) return line;
        const sizes = [...(line.sizes || [])];
        sizes[sizeIndex] = {
          ...sizes[sizeIndex],
          size: name,
        };
        return recalcLine({ ...line, sizes });
      })
    );
  };

  const addSizeRow = (itemId) => {
    setSaleItems(prev =>
      prev.map(line =>
        line.id === itemId
          ? recalcLine({ ...line, sizes: [...(line.sizes || []), { size: '', quantity: 0 }] })
          : line
      )
    );
  };

  const removeSizeRow = (itemId, idx) => {
    setSaleItems(prev =>
      prev.map(line => {
        if (line.id !== itemId) return line;
        const sizes = (line.sizes || []).filter((_, i) => i !== idx);
        return recalcLine({ ...line, sizes });
      })
    );
  };

  const updateColorQty = (itemId, colorIndex, newQty) => {
    setSaleItems(prev =>
      prev.map(line => {
        if (line.id !== itemId) return line;
        const colors = [...(line.colors || [])];
        colors[colorIndex] = { ...colors[colorIndex], quantity: Number(newQty || 0) };
        // NOTE: totals remain defined by sizes.
        return recalcLine({ ...line, colors });
      })
    );
  };

  const updateColorName = (itemId, colorIndex, name) => {
    setSaleItems(prev =>
      prev.map(line => {
        if (line.id !== itemId) return line;
        const colors = [...(line.colors || [])];
        colors[colorIndex] = { ...colors[colorIndex], color: name };
        return recalcLine({ ...line, colors });
      })
    );
  };

  const addColorRow = (itemId) => {
    setSaleItems(prev =>
      prev.map(line =>
        line.id === itemId
          ? recalcLine({ ...line, colors: [...(line.colors || []), { color: '', quantity: 0 }] })
          : line
      )
    );
  };

  const removeColorRow = (itemId, idx) => {
    setSaleItems(prev =>
      prev.map(line => {
        if (line.id !== itemId) return line;
        const colors = (line.colors || []).filter((_, i) => i !== idx);
        return recalcLine({ ...line, colors });
      })
    );
  };

  // price/discount per line
  const updateItemField = (id, field, value) => {
    setSaleItems(prev =>
      prev.map(line => {
        if (line.id !== id) return line;
        const next = { ...line, [field]: Number(value || 0) };
        return recalcLine(next);
      })
    );
  };

  const removeItem = (id) => {
    setSaleItems(prev => prev.filter(i => i.id !== id));
  };

  // totals
  const subtotal = saleItems.reduce((sum, item) => sum + Number(item.total || 0), 0);
  const discountOnAll = discountType === 'percent'
    ? (subtotal * Number(globalDiscount || 0)) / 100
    : Number(globalDiscount || 0);
  const grandTotal = subtotal + Number(otherCharges || 0) - discountOnAll;
  const totalPaying = Number(paidAmount || 0) + Number(newPaidAmount || 0);
  const dueAmount = Math.max(grandTotal - totalPaying, 0).toFixed(2);
  const paymentStatus =
    totalPaying >= grandTotal ? 'paid' :
    totalPaying > 0             ? 'partial' : 'unpaid';

  // form validation
  const validateForm = () => {
    const newErrors = {};
    if (!selectedCustomer) newErrors.customer = "Customer is required";
    if (!status) newErrors.status = "Status is required";
    if (!paymentBank) newErrors.paymentBank = "Payment Bank is required";
    if (saleItems.length === 0) newErrors.items = "Add at least one item";

    // require at least one size qty > 0 in the whole sale
    const anyQty = saleItems.some(l => (l.sizes || []).some(s => Number(s.quantity) > 0));
    if (!anyQty) newErrors.items = "Enter quantity in Sizes (colors track the same quantity).";

    // if any colors are present for a line, their sum must equal the sizes total
    const colorMismatch = saleItems.some(line => {
      const hasAnyColor = (line.colors || []).some(c => (c.color || '').trim().length);
      if (!hasAnyColor) return false;
      const sizeSum = (line.sizes || []).reduce((s, r) => s + Number(r.quantity || 0), 0);
      const colorSum = (line.colors || []).reduce((s, c) => s + Number(c.quantity || 0), 0);
      return sizeSum !== colorSum;
    });
    if (colorMismatch) {
      newErrors.items = "For each item, the total of Color quantities must equal the total Size quantities.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // submit
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    const payload = {
      customer_id: selectedCustomer,
      status,
      reference,
      note,
      sale_date: saleDate,
      bank_id: paymentBank,
      other_charges: Number(otherCharges || 0),
      discount: Number(globalDiscount || 0),
      discount_type: discountType,
      paid_amount: Number(newPaidAmount || 0),
      payment_status: paymentStatus,
      items: saleItems.map(i => {
        const sizes = (i.sizes || [])
          .filter(s => (s.size || '').trim().length)
          .map(s => ({ size: s.size.trim(), quantity: Number(s.quantity || 0) }));

        const colors = (i.colors || [])
          .filter(c => (c.color || '').trim().length)
          .map(c => ({ color: c.color.trim(), quantity: Number(c.quantity || 0) }));

        return {
          item_id: i.id,
          price: Number(i.price || 0),
          discount: Number(i.discount || 0),
          total: Number(i.total || 0),
          ...(sizes.length ? { sizes } : {}),
          ...(colors.length ? { colors } : {}),
        };
      })
    };

    const apiUrl = saleId ? `/sales/${saleId}` : '/sales';
    const apiCall = saleId ? axios.put(apiUrl, payload) : axios.post(apiUrl, payload);

    apiCall
      .then(res => {
        if (res.data.success === true) {
          toast.success(res.data?.message || `Sale ${saleId ? 'updated' : 'created'} successfully!`);
          navigate('/sales-list');
        } else {
          toast.error(res.data?.message || 'Failed to save sale');
        }
      })
      .catch(err => {
        toast.error(err?.response?.data?.error || err.message || 'Error');
      })
      .finally(() => setLoading(false));
  };

  const filteredItems = searchTerm.length >= 1
    ? items.filter(i => i.name.toLowerCase().includes(searchTerm.toLowerCase()))
    : [];

  return (
    <div className="relative p-4 max-w-6xl mx-auto bg-white rounded">
      <h2 className="text-xl font-bold mb-4">Sales — Add/Update</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Top Form Section */}
        <div className="grid md:grid-cols-4 gap-4 mb-4">
          <div>
            <label className="font-semibold text-sm">Sales Date</label>
            <Flatpickr
              value={saleDate}
              className="border w-full rounded"
              onChange={(date) => setSaleDate(date[0])}
            />
          </div>
          <div>
            <label className="font-semibold text-sm">Customer Name *</label>
            <Select
              name="customer"
              options={customersOption}
              placeholder="Select Customer"
              value={customersOption.find(c => c.value === selectedCustomer) || null}
              onChange={e => {
                setSelectedCustomer(e.value);
                if (errors.customer) setErrors(prev => ({ ...prev, customer: null }));
              }}
              classNamePrefix="react-select"
              maxMenuHeight={150}
              className={errors.customer ? 'border border-red-500' : ''}
            />
            {errors.customer && <p className="text-red-500 text-xs">{errors.customer}</p>}
          </div>
          <div>
            <label className="font-semibold text-sm">Status</label>
            <Select
              name="status"
              options={salesOptions}
              placeholder="Select Status"
              value={salesOptions.find(v => v.value === status) || null}
              onChange={e => {
                setStatus(e.value);
                if (errors.status) setErrors(prev => ({ ...prev, status: null }));
              }}
              classNamePrefix="react-select"
              maxMenuHeight={150}
              className={errors.status ? 'border border-red-500' : ''}
            />
            {errors.status && <p className="text-red-500 text-xs">{errors.status}</p>}
          </div>
          <div>
            <label className="font-semibold text-sm">Reference No</label>
            <input
              type="text"
              value={reference}
              placeholder='Enter reference'
              onChange={(e) => setReference(e.target.value)}
              className="border w-full rounded"
            />
          </div>
        </div>

        {/* Item Search */}
        <div className="mb-2 relative" ref={dropdownRef}>
          <input
            type="text"
            placeholder="Enter Item name/Itemcode"
            className="border w-full rounded"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setShowDropdown(e.target.value.length > 0);
            }}
          />
          {errors.items && <p className="text-red-500 text-xs mt-1">{errors.items}</p>}
          {showDropdown && filteredItems.length > 0 && (
            <div className="absolute z-50 bg-white shadow w-full border mt-1 rounded max-h-48 overflow-y-auto">
              {filteredItems.map(item => (
                <div
                  key={item.id}
                  className="p-2 hover:bg-gray-100 cursor-pointer flex justify-between"
                  onClick={() => handleAddItem(item)}
                >
                  <span>{item.name}</span>
                  <span className="text-gray-500">{item.sales_price} BDT</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sale Items Table */}
        {saleItems.length > 0 && (
          <table className="w-full mt-4 border text-xs">
            <thead className="bg-blue-800 text-white">
              <tr>
                <th className="border px-2 py-1">Item Name</th>
                <th className="border px-2 py-1">Sizes (define qty)</th>
                <th className="border px-2 py-1">Colors (must total same qty)</th>
                <th className="border px-2 py-1">Unit Price</th>
                <th className="border px-2 py-1">Discount</th>
                <th className="border px-2 py-1">Total Amount</th>
                <th className="border px-2 py-1">Action</th>
              </tr>
            </thead>
            <tbody>
              {saleItems.map(item => {
                const sizeSum = (item.sizes || []).reduce((s, r) => s + Number(r.quantity || 0), 0);
                const colorSum = colorsTotal(item);
                const mismatch = (item.colors || []).some(c => (c.color || '').trim().length) && sizeSum !== colorSum;

                return (
                  <tr key={item.id}>
                    <td className="border px-2 py-1 text-blue-600 font-medium">{item.name}</td>

                    {/* Sizes */}
                    <td className="border px-2 py-1">
                      {(item.sizes || []).map((s, idx) => (
                        <div key={idx} className="flex gap-1 items-center mb-1">
                          <input
                            type="text"
                            value={s.size}
                            onChange={(e) => updateSizeName(item.id, idx, e.target.value)}
                            className="w-16 border px-1 rounded"
                            placeholder="Size"
                          />
                          <input
                            type="number"
                            min="0"
                            value={s.quantity}
                            onChange={(e) => updateSizeQty(item.id, idx, e.target.value)}
                            className="w-16 border text-center rounded"
                            placeholder="Qty"
                          />
                          <button
                            type="button"
                            onClick={() => removeSizeRow(item.id, idx)}
                            className="text-[10px] text-red-600"
                          >
                            remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="text-xs text-blue-600 mt-1"
                        onClick={() => addSizeRow(item.id)}
                      >
                        + Add Size
                      </button>
                    </td>

                    {/* Colors */}
                    <td className="border px-2 py-1">
                      {(item.colors || []).map((c, idx) => (
                        <div key={idx} className="flex gap-1 items-center mb-1">
                          <input
                            type="text"
                            value={c.color}
                            onChange={(e) => updateColorName(item.id, idx, e.target.value)}
                            className="w-20 border px-1 rounded"
                            placeholder="Color"
                          />
                          <input
                            type="number"
                            min="0"
                            value={c.quantity}
                            onChange={(e) => updateColorQty(item.id, idx, e.target.value)}
                            className="w-16 border text-center rounded"
                            placeholder="Qty"
                          />
                          <button
                            type="button"
                            onClick={() => removeColorRow(item.id, idx)}
                            className="text-[10px] text-red-600"
                          >
                            remove
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="text-xs text-blue-600 mt-1"
                        onClick={() => addColorRow(item.id)}
                      >
                        + Add Color
                      </button>
                      <div className={`mt-1 text-[10px] ${mismatch ? 'text-red-600' : 'text-gray-500'}`}>
                        Size total: {sizeSum} | Color total: {colorSum}
                        {mismatch && ' — must match'}
                      </div>
                    </td>

                    {/* price, discount, total */}
                    <td className="border px-2 py-1">
                      <input
                        type="number"
                        value={item.price}
                        onChange={(e) => updateItemField(item.id, 'price', e.target.value)}
                        className="border p-1 rounded text-center w-24"
                      />
                    </td>
                    <td className="border px-2 py-1">
                      <input
                        type="number"
                        value={item.discount}
                        onChange={(e) => updateItemField(item.id, 'discount', e.target.value)}
                        className="border p-1 rounded text-center w-24"
                      />
                    </td>
                    <td className="border px-2 py-1 text-right font-semibold">
                      {Number(item.total || 0).toFixed(2)}
                    </td>
                    <td className="border px-2 py-1 text-center">
                      <TrashIcon onClick={() => removeItem(item.id)} className="h-4 w-4 text-red-500 cursor-pointer" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Charges & Payment */}
        <div className="grid md:grid-cols-3 gap-4 mt-4">
          <div>
            <label className="font-semibold text-sm">Other Charges</label>
            <input
              type="number"
              value={otherCharges}
              placeholder='Enter other charge'
              onChange={(e) => setOtherCharges(Number(e.target.value || 0))}
              className="border w-full rounded"
            />
          </div>
          <div>
            <label className="font-semibold text-sm">Discount on All</label>
            <div className="flex gap-2">
              <input
                type="number"
                value={globalDiscount}
                onChange={(e) => setGlobalDiscount(Number(e.target.value || 0))}
                className="w-full border rounded"
              />
              <select
                value={discountType}
                onChange={(e) => setDiscountType(e.target.value)}
                className="border rounded"
              >
                <option value="percent">%</option>
                <option value="flat">Flat</option>
              </select>
            </div>
          </div>
          <div>
            <label className="font-semibold text-sm">Payment Bank</label>
            <Select
              name="paymentBank"
              options={banks}
              value={banks.find(v => v.value === paymentBank) || null}
              placeholder="Select Bank"
              classNamePrefix="react-select"
              maxMenuHeight={150}
              onChange={e => {
                setPaymentBank(e.value);
                if (errors.paymentBank) setErrors(prev => ({ ...prev, paymentBank: null }));
              }}
              className={errors.paymentBank ? 'border border-red-500' : ''}
            />
            {errors.paymentBank && <p className="text-red-500 text-xs">{errors.paymentBank}</p>}
          </div>
        </div>

        {/* Summary */}
        <div className="text-right mt-6 space-y-1">
          <div>Subtotal: <strong>{subtotal.toFixed(2)}</strong></div>
          <div>Other Charges: <strong>{Number(otherCharges || 0).toFixed(2)}</strong></div>
          <div>Discount: <strong>{discountOnAll.toFixed(2)}</strong></div>
          <div>Due Amount: <strong>{dueAmount} BDT</strong></div>
          <div className="text-lg font-bold">Grand Total: <strong>{grandTotal.toFixed(2)} BDT</strong></div>
        </div>

        {/* Paid & Note */}
        <div className="mt-4">
          <label className="font-semibold text-sm">Paid Amount</label>
          <input
            type="number"
            className="border rounded w-full"
            value={newPaidAmount}
            onChange={(e) => setNewPaidAmount(Number(e.target.value || 0))}
          />
          <p className="text-sm text-gray-600 mt-1">Payment Status: <b>{paymentStatus}</b></p>
        </div>
        <div>
          <label className="font-semibold text-sm">Note</label>
          <textarea
            className="w-full border rounded"
            rows="2"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-4 mt-4">
          <button disabled={loading} type='submit' className="bg-pink-600 hover:bg-pink-700 text-white px-6 py-1 rounded">
            {loading ? "Submitting..." : "Save"}
          </button>
          <button
            type="button"
            onClick={() => navigate('/sales-list')}
            className="bg-gray-400 hover:bg-gray-500 text-white px-6 py-1 rounded"
          >
            Close
          </button>
        </div>
      </form>

      <Loader show={loading} />
    </div>
  );
};

export default CreateSale;